import assert from 'assert';
import { Loading } from '@livecontrol/core-ui';
import { Str } from '@livecontrol/core-utils';
import _ from 'lodash';
import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useParams } from 'react-router';
import styled from 'styled-components';
import shallow from 'zustand/shallow';
import { Panel } from '../../components';
import { useCcConfig } from '../../store';
import type { CardSizeLevel } from './card-size';
import { cardColumns, cardMaxWidth, toCardSizeLevel } from './card-size';
import { CardV2 } from './card-v2';
import type { UseEventsHook, useUntitledEvents} from './graphql';
import { useFavoriteEvents, useLiveEvents, useMyEventsWithFavorites, usePastEvents, useUpcomingEvents } from './graphql';
import { MyEventsStats } from './my-events-stats';
import type { EventFilters } from './store';
import { useEventsStore } from './store';
import { Tab } from './tabber';
import { EventsTable } from './table';
import { EventsToolbar } from './toolbar';

const ResetButton = styled.a``;

// Browser time zone, shown above the filters (same bar as the event page, minus the back link).
const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 2px;

  .tz {
    color: var(--white40);
    font-size: 13px;
    white-space: nowrap;

    b {
      font-weight: 700;
    }
  }
`;

type UntitledEvents = Exclude<ReturnType<typeof useUntitledEvents>['untitledEvents'], undefined>;

interface TabConfiguration {
  hook: UseEventsHook;
  showVideo: boolean;
  enableFavorite: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  showLargeItems: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  showFilters: { sort: boolean; dateRange: boolean; eventType: boolean };
  defaultFilters?: Partial<EventFilters>;
}

/* Responsive card grid; columns/caps follow the card-size slider level. */
const CardsGrid = styled.div<{ $level: CardSizeLevel }>`
  display: grid;
  grid-template-columns: ${({ $level }): string => cardColumns($level)};
  gap: 16px;

  /* Cap each card at the level's max; the 1fr columns handle the growth. */
  > * {
    width: 100%;
    max-width: ${({ $level }): string => cardMaxWidth($level)};
    margin-inline: auto;
  }
`;


const EventsWrapper = ({
  hook,
  showVideo,
  enableFavorite,
  defaultFilters
}: TabConfiguration): ReactElement | null => {

  const { filters, favoriteEvents, eventConfig, toggleFavoriteEvent, storeEventVolume, resetFilters } =
    useEventsStore((v) => ({
      filters: { ...v.filters, search: v.filters.search?.trim() },
      favoriteEvents: v.favoriteEvents,
      toggleFavoriteEvent: v.toggleFavoriteEvent,
      resetFilters: v.resetFilters,
      eventConfig: v.eventConfig,
      storeEventVolume: v.storeEventVolume
    }), shallow);

  const { cardSize: cardSizeValue, viewMode, autoplayCards } = useCcConfig((v) => ({
    cardSize: v.cardSize,
    viewMode: v.viewMode,
    autoplayCards: v.autoplayCards
  }), shallow);

  const cardSize = toCardSizeLevel(cardSizeValue);

  /*
   * One handler for the whole grid instead of an arrow per card. The store actions are stable, so
   * these are too, and a memoised card only re-renders when its own data changes.
   */
  const handleToggleFavorite = useCallback(
    (eid: string): void => {
      toggleFavoriteEvent(eid);
    },
    [toggleFavoriteEvent]
  );

  const handleVolumeChange = useCallback(
    (eid: string, volume: number): void => {
      storeEventVolume(eid, volume);
    },
    [storeEventVolume]
  );

  // A tab may allow live video (showVideo); the global toggle can turn it off.
  const playVideo = showVideo && autoplayCards;

  const response = hook(filters);
  const { events, loading, error, refetch, loadMore, hasMore } = response;
  const { untitledEvents } = (response as unknown) as { untitledEvents?: UntitledEvents };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px'
  });

  const noResultsFound = useMemo(
    () =>
      filters.search || (filters.dateRange?.min && filters.dateRange.max) || filters.eventType ? (
        <Panel className='text-center p-16px text-white'>
          <p>
            No results match your filter criteria.{' '}
            <ResetButton
              className='x-pseudo cursor-pointer text-white text-decoration-underline'
              onClick={(e: { preventDefault: () => void }): void => {
                e.preventDefault();
                resetFilters(defaultFilters);
              }}
            >
              Click Here
            </ResetButton>{' '}
            to reset your criteria.
          </p>
        </Panel>
      ) : (
        <Panel className='text-center p-16px text-white'>No Events Found.</Panel>
      ),
    [defaultFilters, filters, resetFilters]
  );

  if (error) {
    return (
      <Panel className='text-center p-16px text-danger'>
        Error trying to load events.{' '}
        <ResetButton
          className='x-pseudo cursor-pointer text-white text-decoration-underline'
          onClick={(e: { preventDefault: () => void }): void => {
            e.preventDefault();
            void refetch();
          }}
        >
          Click Here to retry
        </ResetButton>
      </Panel>
    );
  } else if ((events?.length === 0 && (!untitledEvents || untitledEvents.length === 0)) && !hasMore) {
    return noResultsFound;
  }

  return (
    <>
      {viewMode === 'table' ? (
        <EventsTable
          events={events}
          untitledEvents={untitledEvents}
          favoriteEvents={favoriteEvents}
          search={filters.search}
        />
      ) : (
        <CardsGrid $level={cardSize}>
          {events?.map((event) => (
            <CardV2
              key={event.eid}
              event={event}
              search={filters.search}
              size={cardSize}
              enableFavorite={enableFavorite}
              showVideo={playVideo}
              isFavorite={favoriteEvents.has(event.eid)}
              initialVolume={eventConfig.get(event.eid)?.volume}
              onToggleFavorite={handleToggleFavorite}
              onVolumeChange={handleVolumeChange}
            />
          ))}
          {untitledEvents?.map((event) => (
            <CardV2
              key={event.id}
              event={event}
              search={filters.search}
              size={cardSize}
              showVideo={playVideo}
            />
          ))}
        </CardsGrid>
      )}
      {(hasMore || loading || (events?.length === 0 && untitledEvents?.length === 0)) && (
        <div ref={sentryRef} className='mt-12px'>
          <Loading isOldVersion />
        </div>
      )}
    </>
  );
};

export const Events = (): React.ReactElement => {
  const slug = Str.normalize(useParams<{ slug?: string }>().slug) ?? 'live';

  const updateFilters = useEventsStore((v) => v.updateFilters);

  const tabMap: Record<string, Tab> = {
    favorite: Tab.Favorite,
    past: Tab.Past,
    upcoming: Tab.Upcoming,
    live: Tab.Live,
    mine: Tab.Mine
  };

  const tab = tabMap[slug];

  const TabConfigurationMap: Record<Tab, TabConfiguration | undefined> = useMemo(
    () => ({
      [Tab.Favorite]: {
        hook: useFavoriteEvents,
        showFilters: { eventType: true, sort: true, dateRange: true },
        defaultFilters: { sortBy: 'startTime', sortOrder: 'ASC', limit: 20 },
        showVideo: true,
        enableFavorite: true,
        showLargeItems: true
      },
      [Tab.Live]: {
        hook: useLiveEvents,
        showFilters: { eventType: true, sort: true, dateRange: false },
        defaultFilters: { sortBy: 'startTime', sortOrder: 'ASC', dateRange: {}, limit: 20 },
        showVideo: true,
        enableFavorite: true,
        showLargeItems: false
      },
      [Tab.Upcoming]: {
        hook: useUpcomingEvents,
        showFilters: { eventType: true, sort: true, dateRange: true },
        defaultFilters: { sortBy: 'startTime', sortOrder: 'ASC', dateRange: {}, limit: 20 },
        showVideo: false,
        enableFavorite: true,
        showLargeItems: false
      },
      [Tab.Past]: {
        hook: usePastEvents,
        showFilters: { eventType: true, sort: true, dateRange: true },
        defaultFilters: { sortBy: 'startTime', sortOrder: 'DESC', dateRange: {}, limit:20 },
        showVideo: false,
        enableFavorite: false,
        showLargeItems: false
      },
      [Tab.Mine]: {
        hook: useMyEventsWithFavorites,
        showFilters: { eventType: true, sort: true, dateRange: true },
        defaultFilters: { sortBy: 'startTime', sortOrder: 'ASC', dateRange: {}, limit: 20 },
        showVideo: true,
        enableFavorite: true,
        showLargeItems: false
      }
    }),

    []
  );

  const tabConfiguration = TabConfigurationMap[tab] ?? TabConfigurationMap[Tab.Live];

  assert(tabConfiguration);

  useEffect(() => {
    const { defaultFilters } = tabConfiguration;

    if (defaultFilters) {
      updateFilters(defaultFilters);
    }
  }, [tabConfiguration, updateFilters]);

  return (
    <div key={slug}>
      <TopBar>
        <div className='tz'>
          <b>Time Zone:</b> {DateTime.local().toFormat('z')}
        </div>
      </TopBar>
      <EventsToolbar className='mb-16px' activeSlug={slug} />
      {slug === 'mine' && <MyEventsStats />}
      <EventsWrapper {...tabConfiguration} />
    </div>
  );
};
